import { ArrowDownIcon } from 'components/icons';
import {
    MessageScroller,
    MessageScrollerButton,
    MessageScrollerContent,
    MessageScrollerItem,
    MessageScrollerProvider,
    MessageScrollerViewport,
    useMessageScroller,
} from 'components/ui/message-scroller';
import { Tooltip } from 'components/ui/tooltip';
import { useReducedMotion } from 'hooks/useReducedMotion';
import { type ComponentProps, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';
import type { PortalChatMessage } from 'types/aiChat';
import { cn } from 'utils/cn';
import { AiChatInternalLinkProvider } from '../aiChatInternalLinkContext';
import { AiChatOptionPickHandlers, AiChatOptionPickProvider } from '../aiChatOptionPickContext';
import type { AiChatSurfaceController } from '../AiChatSessionProvider';
import { optionText, parseChooseOptionsSpec } from '../utils/chooseOptionsSpec';
import { getLatestUserText } from '../utils/messageMapping';
import { extractToolCallHistory } from '../utils/toolCallParts';
import AiChatConnectionBanner from './AiChatConnectionBanner';
import AiChatMessageComponent from './AiChatMessage';
import AiChatSendError from './AiChatSendError';

interface Props {
    controller: Pick<
        AiChatSurfaceController,
        | 'userVisibleMessages'
        | 'isStreaming'
        | 'stoppedMessageId'
        | 'threadTitle'
        | 'connectionUnstable'
        | 'failedSend'
        | 'isRetryingFailedSend'
        | 'retryLastMessage'
        | 'editFailedMessage'
    >;
    optionPick: AiChatOptionPickHandlers;
    onInternalLinkNavigate: () => void;
    /** Where an opened chat starts: at its end unless the surface says otherwise. */
    defaultScrollPosition?: ComponentProps<typeof MessageScrollerProvider>['defaultScrollPosition'];
    /** Whether the view follows the live edge; always, unless the surface says otherwise. */
    autoScroll?: boolean;
    viewportClassName?: string;
    contentClassName?: string;
}

/** The texts a pick of one of the message's options sends. */
const offeredTexts = (message: PortalChatMessage) =>
    extractToolCallHistory(message.parts)
        .filter((toolCall) => toolCall.name === 'chooseOptions' && toolCall.status === 'success')
        .flatMap((toolCall) => parseChooseOptionsSpec(toolCall.output)?.questions ?? [])
        .flatMap((question) => question.options.map(optionText));

/**
 * Keeps `--scrollbar-gutter` on the conversation at the width its scrollbar reserves, 0 for an overlay one: the
 * content's end padding gives it back, so a Figure sits as far from either edge as the boards draw it.
 */
function trackGutter(viewport: HTMLDivElement | null) {
    if (!viewport || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() =>
        viewport.style.setProperty('--scrollbar-gutter', `${viewport.offsetWidth - viewport.clientWidth}px`)
    );
    observer.observe(viewport);
    return () => observer.disconnect();
}

/**
 * Brings a failed send's notice, the conversation's last line, into view as it shows. A frame later, after the
 * scroller's own response: it reads the connection banner giving way to the notice as one item swapped for another,
 * and scrolls to the first question it has not scrolled to yet, which in a reopened chat is its first.
 */
function NoticeInView({ failure }: { failure: unknown }) {
    const { scrollToEnd } = useMessageScroller();
    useLayoutEffect(() => {
        if (!failure) return undefined;
        const frame = requestAnimationFrame(() => scrollToEnd());
        return () => cancelAnimationFrame(frame);
    }, [failure, scrollToEnd]);
    return null;
}

/** A chat's messages as the panel, the page and the phone show them, with its send error and Jump to latest. */
export default function AiChatConversation({
    controller,
    optionPick,
    onInternalLinkNavigate,
    defaultScrollPosition,
    autoScroll = true,
    viewportClassName,
    contentClassName,
}: Props) {
    const intl = useIntl();
    const reducedMotion = useReducedMotion();
    const {
        userVisibleMessages,
        isStreaming,
        stoppedMessageId,
        threadTitle,
        connectionUnstable,
        failedSend,
        isRetryingFailedSend,
        retryLastMessage,
        editFailedMessage,
    } = controller;
    const lastIndex = userVisibleMessages.length - 1;
    const viewportRef = useRef<HTMLDivElement>(null);
    useEffect(() => trackGutter(viewportRef.current), []);
    // The questions that are a pick from the choice the answer before them offered.
    const pickedIds = useMemo(
        () =>
            new Set(
                userVisibleMessages.flatMap((message, index) => {
                    const previous = userVisibleMessages[index - 1];
                    return message.role === 'user' &&
                        previous?.role === 'assistant' &&
                        offeredTexts(previous).includes(getLatestUserText([message]))
                        ? [message.id]
                        : [];
                })
            ),
        [userVisibleMessages]
    );
    const jumpLabel = intl.formatMessage({ id: 'ai-chat.jump-to-latest' });

    return (
        <AiChatInternalLinkProvider value={onInternalLinkNavigate}>
            <AiChatOptionPickProvider value={optionPick}>
                {/* A sent question settles near the top and its answer streams in under it, followed while the
                    reader stays at the live edge. */}
                <MessageScrollerProvider autoScroll={autoScroll} defaultScrollPosition={defaultScrollPosition}>
                    <NoticeInView failure={failedSend} />
                    <MessageScroller className="flex-1">
                        <MessageScrollerViewport
                            ref={viewportRef}
                            aria-label={intl.formatMessage({ id: 'ai-chat.conversation' })}
                            // The connection banner sticks to the top: what takes focus scrolls clear of it.
                            className={cn('overflow-x-hidden', viewportClassName, connectionUnstable && 'scroll-pt-8')}
                        >
                            <MessageScrollerContent
                                className={cn(
                                    'gap-6 ps-4 pe-(--content-end) pt-4 pb-6 [--content-end:max(0px,calc(1rem-var(--scrollbar-gutter,0px)))]',
                                    contentClassName
                                )}
                            >
                                {connectionUnstable ? <AiChatConnectionBanner /> : null}
                                {userVisibleMessages.map((message, index) => (
                                    <MessageScrollerItem
                                        key={message.id}
                                        messageId={message.id}
                                        scrollAnchor={message.role === 'user'}
                                        // The item clips what it paints; 8px each side keeps the actions' hover
                                        // fill and focus ring, which sit past the message's edge, whole. At the end,
                                        // no more than the padding a scrollbar's gutter leaves, or it would overflow.
                                        className="-ms-2 -me-[min(0.5rem,var(--content-end))] ps-2 pe-[min(0.5rem,var(--content-end))]"
                                    >
                                        <AiChatMessageComponent
                                            message={message}
                                            isStreaming={
                                                isStreaming && message.role === 'assistant' && index === lastIndex
                                            }
                                            stopped={message.id === stoppedMessageId}
                                            isLastMessage={index === lastIndex}
                                            threadTitle={threadTitle}
                                            picked={pickedIds.has(message.id)}
                                        />
                                    </MessageScrollerItem>
                                ))}
                                {failedSend ? (
                                    <AiChatSendError
                                        failure={failedSend}
                                        isRetrying={isRetryingFailedSend}
                                        onRetry={() => void retryLastMessage()}
                                        onEditMessage={editFailedMessage}
                                    />
                                ) : null}
                            </MessageScrollerContent>
                        </MessageScrollerViewport>
                        <Tooltip content={jumpLabel}>
                            <MessageScrollerButton
                                variant="outline"
                                aria-label={jumpLabel}
                                // A scripted smooth scroll ignores the reduced-motion setting, so it is asked for here.
                                behavior={reducedMotion ? 'auto' : 'smooth'}
                                // On the motion scale, out faster than in; under reduced motion it only fades.
                                className="duration-(--duration-fast) data-[active=false]:duration-(--duration-quick) data-[active=false]:ease-(--ease-smooth-out) data-[active=true]:ease-(--ease-smooth-out) motion-reduce:transition-opacity motion-reduce:duration-(--duration-quick)"
                            >
                                <ArrowDownIcon />
                            </MessageScrollerButton>
                        </Tooltip>
                    </MessageScroller>
                </MessageScrollerProvider>
            </AiChatOptionPickProvider>
        </AiChatInternalLinkProvider>
    );
}
